<template>
  <div class="ui-input">
    <input
      v-if="formFieldName"
      type="hidden"
      :name="formFieldName"
      :value="valueAsJson"
    />

    <div
      v-if="valueArray.length"
      class="uk-alert-warning"
      style="font-size: 14px; color: #000;"
      uk-alert
    >
      <b>Files uploaded:</b>
      <ul style="margin-top: 0; margin-bottom: 0;">
        <li v-for="(val, idx) in valueArray" :key="idx">
          {{ val }}
          <button
            type="button"
            class="uk-button uk-button-link uk-button-small"
            aria-label="Remove"
            @click="removeFile(val)"
          >
            <span uk-icon="trash"></span>
          </button>
        </li>
      </ul>
    </div>

    <uppy-dashboard
      :uppy="uppy"
      :props="dashboardProps"
    />
  </div>
</template>

<script>
import { Dashboard } from '@uppy/vue'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import * as Sentry from '@sentry/vue'

export default {
  name: 'UiFormUppy',

  components: {
    UppyDashboard: Dashboard,
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  inject: {
    ngForm: {
      default: '',
    },
  },

  props: {
    modelValue: {
      type: Array,
      default() {
        return []
      },
    },
    paramsUrl: {
      type: String,
      required: true,
    },
    formFieldName: {
      type: String,
    },
    minNumFiles: {
      type: Number,
      default: 0,
    },
    maxNumFiles: {
      type: Number,
      default: 1,
    },
    allowedTypes: {
      type: Array,
    },
    autoUpload: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uppy: null,
    }
  },

  computed: {
    multipleFilesAllowed() {
      return this.maxNumFiles === -1 || this.maxNumFiles > 1
    },

    valueArray() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue
      }
      if (this.modelValue) {
        return [this.modelValue]
      }
      return []
    },

    valueAsJson() {
      return JSON.stringify(this.valueArray)
    },

    shouldShowUploader() {
      return this.valueArray.length < this.maxNumFiles
    },

    dashboardProps() {
      const browseText = this.multipleFilesAllowed
        ? 'Drop files here, paste, or %{browse}'
        : 'Drop file here, paste, or %{browse}'

      return {
        theme: 'light',
        doneButtonHandler: null,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false,
        locale: {
          strings: {
            dropPasteFiles: browseText,
          },
        },
      }
    },
  },

  methods: {
    buildUploadContext(file = null) {
      const context = {
        formFieldName: this.formFieldName || '',
      }

      if (file) {
        context.fileExtension = file.extension || ''
        context.fileName = file.name || ''
        context.fileSize = file.size || null
        context.fileType = file.type || ''
        context.uppyFileId = file.id || ''
      }

      return context
    },

    logUploadBreadcrumb(message, data = {}) {
      Sentry.addBreadcrumb({
        category: 'upload',
        data,
        level: 'info',
        message,
      })
    },

    logUploadInfo(eventName, context = {}) {
      if (!Sentry.logger || typeof Sentry.logger.info !== 'function') {
        return
      }

      Sentry.logger.info(`front-form-upload: ${eventName}`, context)
    },

    captureUploadException(error, eventName, context = {}) {
      const errorToCapture = error instanceof Error ? error : new Error(`Upload failure: ${eventName}`)

      Sentry.withScope((scope) => {
        scope.setTag('frontend_feature', 'form_upload')
        scope.setTag('upload_event', eventName)
        scope.setExtra('uploadContext', context)

        if (!(error instanceof Error)) {
          scope.setExtra('rawError', error)
        }

        Sentry.captureException(errorToCapture)
      })
    },

    removeFile(val) {
      let values = this.valueArray.slice()
      const idx = values.indexOf(val)
      if (idx !== -1) {
        values.splice(idx, 1)
        this.modelValue = values
        this.$emit('update:modelValue', values)
      }
    },
  },

  beforeDestroy() {
    this.uppy.close()
  },

  created() {
    const uppy = new Uppy({
      debug: false,
      autoProceed: this.autoUpload,
      allowMultipleUploadBatches: this.multipleFilesAllowed,
      restrictions: {
        // bytes = {X}MB * 1E+6;  1E+6 === 1,000,000
        maxFileSize: 50 * 1000000,
        maxNumberOfFiles: this.maxNumFiles === -1 ? null : this.maxNumFiles,
        minNumberOfFiles: this.minNumFiles,
        allowedFileTypes: this.allowedTypes,
      },
    }).use(AwsS3, {
      getUploadParameters: (file) => {
        const uploadContext = this.buildUploadContext(file)
        this.logUploadInfo('upload-params-requested', uploadContext)

        return fetch(this.paramsUrl, {
          method: 'post',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
          }),
        })
          .then(async (response) => {
            let data = null

            try {
              data = await response.json()
            } catch (error) {
              const parseError = new Error('Failed to parse upload params response')
              parseError.uploadContext = {
                ...uploadContext,
                responseStatus: response.status,
              }
              throw parseError
            }

            if (!response.ok) {
              const errorMessage = data && typeof data.detail === 'string'
                ? data.detail
                : `Upload params request failed (${response.status})`
              const requestError = new Error(errorMessage)
              requestError.uploadContext = {
                ...uploadContext,
                responseBodyKeys: data && typeof data === 'object' ? Object.keys(data) : [],
                responseStatus: response.status,
              }
              throw requestError
            }

            if ('error' in data) {
              alert(data.error)
              const apiError = new Error(data.error)
              apiError.uploadContext = {
                ...uploadContext,
                responseBodyKeys: Object.keys(data),
                responseStatus: response.status,
              }
              throw apiError
            }

            this.logUploadInfo('upload-params-received', {
              ...uploadContext,
              responseStatus: response.status,
            })

            return {
              method: data.method,
              url: data.url,
              fields: data.fields,
            }
          })
          .catch((error) => {
            const errorContext = {
              ...uploadContext,
              ...(error && error.uploadContext ? error.uploadContext : {}),
            }
            this.logUploadBreadcrumb('upload-params-failure', errorContext)
            this.captureUploadException(error, 'upload-params-failure', errorContext)
            throw error
          })
      },
    })

    uppy.on('upload', (data) => {
      this.logUploadInfo('upload-started', {
        ...this.buildUploadContext(),
        fileCount: data.fileIDs.length,
        uploadId: data.id,
      })
    })

    uppy.on('upload-success', (file) => {
      this.logUploadInfo('upload-success', {
        ...this.buildUploadContext(file),
      })

      let values = this.valueArray.slice()
      if (!this.multipleFilesAllowed) {
        values = [file.meta.filename]
      } else {
        values.push(file.meta.filename)
      }
      this.$emit('update:modelValue', values)
    })

    uppy.on('upload-error', (file, error, response) => {
      const errorContext = {
        ...this.buildUploadContext(file),
        responseBodyKeys: response && response.body && typeof response.body === 'object' ? Object.keys(response.body) : [],
        responseStatus: response && response.status ? response.status : null,
      }

      this.logUploadBreadcrumb('upload-error', errorContext)
      this.captureUploadException(error, 'upload-error', errorContext)
    })

    uppy.on('restriction-failed', (file, error) => {
      const errorContext = this.buildUploadContext(file)
      this.logUploadBreadcrumb('upload-restriction-failed', errorContext)
      this.captureUploadException(error, 'upload-restriction-failed', errorContext)
    })

    uppy.on('complete', (result) => {
      this.logUploadInfo('upload-complete', {
        ...this.buildUploadContext(),
        failedCount: result.failed.length,
        successCount: result.successful.length,
      })
    })

    this.uppy = uppy
  },
}
</script>

<style src="@uppy/core/dist/style.css"></style>
<style src="@uppy/dashboard/dist/style.css"></style>
